import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUpdateMonitoringSource, useUpsertMonitoringSource } from "@/hooks/use-monitoring-sources";
import {
  useConfirmMonitoringIdentifier,
  useResetMonitoringOverride,
  useToggleMonitoringSource,
} from "@/hooks/use-monitoring-source-actions";
import { formatNOK, formatRelativeTime } from "@/lib/format";
import {
  canToggleSource,
  FREQUENCY_LABELS,
  type MonitoringSourceMeta,
  monitoringMode,
  monitoringRunStatus,
} from "@/lib/monitoring-sources";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";
import { TONE_TEXT } from "@/lib/widget-status";
import type { DBCustomerMonitoringSource, SignalFrequency } from "@/lib/db-types";

interface Props {
  customerId: string;
  meta: MonitoringSourceMeta;
  row: DBCustomerMonitoringSource | undefined;
}

/** Én kilde i dialogen «Rediger kilder»: adresse, bryter, modus, frekvens og driftsstatus. */
export default function MonitoringSourceRow({ customerId, meta, row }: Props) {
  const [draft, setDraft] = useState<string | null>(null);
  const upsert = useUpsertMonitoringSource();
  const update = useUpdateMonitoringSource();
  const toggle = useToggleMonitoringSource();
  const reset = useResetMonitoringOverride();
  const confirm = useConfirmMonitoringIdentifier();

  const identifier = draft ?? row?.identifier ?? "";
  // Tom verdi kan ikke lagres (en kilde uten adresse har ingenting å hente), så knappen skjules.
  const dirty = draft !== null && draft.trim() !== "" && draft.trim() !== (row?.identifier ?? "");
  const suggestedByAgent = row?.identifier_source === "agent";
  const mode = row ? monitoringMode(row) : null;
  const status = monitoringRunStatus(row);

  const saveIdentifier = () => {
    const value = identifier.trim();
    if (!value) return;
    const done = { onSuccess: () => setDraft(null) };
    if (row) update.mutate({ row, patch: { identifier: value } }, done);
    else upsert.mutate({ customerId, source: meta.kind, identifier: value }, done);
  };

  return (
    <li className="space-y-1.5 border-b border-border/50 py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-24 font-medium">{meta.label}</div>
        <Input
          className="h-8 min-w-[12rem] flex-1"
          value={identifier}
          placeholder={meta.placeholder}
          aria-label={`Adresse eller handle for ${meta.label}`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && dirty) saveIdentifier();
          }}
        />
        {dirty && (
          <Button
            size="sm"
            className="h-8"
            aria-label={`Lagre adresse for ${meta.label}`}
            disabled={upsert.isPending || update.isPending}
            onClick={saveIdentifier}
          >
            Lagre
          </Button>
        )}
        <Switch
          checked={row?.is_active ?? false}
          disabled={!canToggleSource(row) || suggestedByAgent || toggle.isPending}
          aria-label={`Overvåk ${meta.label}`}
          onCheckedChange={(isActive) => row && toggle.mutate({ row, isActive })}
        />
        {suggestedByAgent && <Badge variant="outline">Foreslått av agent</Badge>}
        {suggestedByAgent && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            aria-label={`Bekreft foreslått adresse for ${meta.label}`}
            disabled={confirm.isPending}
            onClick={() => row && confirm.mutate(row)}
          >
            Bekreft
          </Button>
        )}
        {mode === "follows_status" && !suggestedByAgent && <Badge variant="outline">Følger status</Badge>}
        {mode === "overridden" && <Badge variant="secondary">Overstyrt</Badge>}
        {status && <StatusBadge tone={status.tone} label={status.label} />}
      </div>
      {row && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pl-[6.75rem] text-xs text-muted-foreground">
          <Select
            value={row.frequency}
            disabled={update.isPending}
            onValueChange={(frequency) => update.mutate({ row, patch: { frequency: frequency as SignalFrequency } })}
          >
            <SelectTrigger className="h-7 w-28 text-xs" aria-label={`Frekvens for ${meta.label}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>{row.last_run_at ? `Sist kjørt ${formatRelativeTime(row.last_run_at)}` : "Ikke kjørt ennå"}</span>
          <span>Kostnad hittil {formatNOK(row.cost_total_nok)}</span>
          {mode === "overridden" && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              aria-label={`Tilbakestill ${meta.label} til automatisk`}
              disabled={reset.isPending}
              onClick={() => reset.mutate(row)}
            >
              Tilbakestill til automatisk
            </Button>
          )}
          {row.last_error && <span className={TONE_TEXT.miss}>Siste feil: {row.last_error}</span>}
        </div>
      )}
    </li>
  );
}
